// Place photos for Babcock Nav.
//
// Hard rule: if there is not a photo clearly of THAT place, do not show an
// image. Wrong photos are worse than no photo. Prefer avatar/initials / no
// banner over a stand-in.
//
// Resolution order (one path, used by Worker fill, PlaceCard, Favorites,
// detail, map popups, guest rows):
//   1. DB `image_url` if it is a non-empty, non-Unsplash URL
//   2. BY_SLUG, only for Wikimedia (or similarly sourced) photos verified
//      as that POI
//   3. null — PlaceCard hides the banner; PlaceAvatar shows initials
//
// Category Unsplash fallbacks are intentionally gone. Do not re-add them.
// Do not invent stock URLs.

/** Wikimedia Commons Special:FilePath, width-capped for card banners. */
export function commons(file: string): string {
  return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=960`;
}

/**
 * Only slugs whose file is verifiably that POI (Commons description /
 * categories name the place). Everything else is omitted so the UI
 * renders initials instead of a mismatched interior or "restaurant vibes".
 */
export const BY_SLUG: Record<string, string> = {
  // Charlotte Sports Park in Port Charlotte, FL (Commons: Stadium6.jpg).
  'charlotte-sports-park': commons('Stadium6.jpg'),
  // Gasparilla Island Light, the landmark on Boca Grande.
  'boca-grande-gasparilla-island': commons('Gasparilla Island Light December 2014.JPG'),
  // Captiva Island beach (Florida Memory / Commons).
  'captiva-island': commons(
    'Couples on the beach at sunset- Captiva Island, Florid (9103803844).jpg',
  ),
  // Charlotte Harbor Preserve State Park (Commons category of the same name).
  'charlotte-harbor-preserve-state-park': commons('Charlotte Harbor Pres SP03.jpg'),
  // Fort Myers Beach sunset.
  'fort-myers-beach': commons('Fort Myers Beach.jpg'),
  // Sanibel Island Light, the island's landmark.
  'sanibel-island': commons('Sanibel Island Light, September 2014.jpg'),
  // Punta Gorda Airport terminal building.
  'punta-gorda-airport-pgd': commons('Punta Gorda Airport terminal.gk.jpg'),
  // Southwest Florida International Airport (RSW) terminal.
  'southwest-florida-international-airport-rsw': commons(
    'Southwest Florida International Airport RSW.jpg',
  ),
};

function urlHost(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Stock Unsplash (and lookalikes) must never become a banner. */
export function isUnsplashUrl(url: string): boolean {
  const host = urlHost(url);
  if (host) {
    return host === 'unsplash.com' || host === 'images.unsplash.com' || host.endsWith('.unsplash.com');
  }
  return /unsplash\.com/i.test(url);
}

export function isWikimediaUrl(url: string): boolean {
  const host = urlHost(url);
  if (host) {
    return host === 'commons.wikimedia.org' || host === 'upload.wikimedia.org' || host.endsWith('.wikimedia.org');
  }
  return /wikimedia\.org/i.test(url);
}

const APPROVED_COMMONS = new Set(Object.values(BY_SLUG));

/**
 * A URL is showable only when it is a real photo of that POI:
 * official / maps / other non-stock hosts, or a Commons file already
 * audited into BY_SLUG. Worker-filled leftovers (wrong Commons, Unsplash)
 * are treated as missing.
 */
function verifiedUrl(url: string | null | undefined): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  if (isUnsplashUrl(trimmed)) return null;
  if (isWikimediaUrl(trimmed) && !APPROVED_COMMONS.has(trimmed)) return null;
  return trimmed;
}

/**
 * Curated POI photo for a slug, or null. Category is ignored — there is
 * no category fallback. Kept on the signature so Worker / tests that
 * still pass it compile.
 */
export function imageForPlace(slug: string, _category?: string): string | null {
  return verifiedUrl(BY_SLUG[slug]);
}

/**
 * Shared resolution for every surface. DB override wins when it is a
 * real (non-Unsplash) URL; otherwise the verified slug map; otherwise null.
 */
export function displayImageUrl(place: {
  image_url?: string | null;
  slug: string;
  category: string;
}): string | null {
  const override = verifiedUrl(place.image_url);
  if (override) return override;
  return imageForPlace(place.slug, place.category);
}
